import { useEffect, useRef, useState } from 'react'
import { Building2, Check, ChevronDown, Plus } from 'lucide-react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useMe, useSwitchOrg } from '../../api/auth'

export function OrgSwitcher() {
  const { t } = useTranslation('auth')
  const { data: me } = useMe()
  const switchOrg = useSwitchOrg()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  if (!me) return null

  const active = me.memberships.find((m) => m.org_id === me.active_org_id)
  const activeName = active?.org?.name ?? t('orgSwitcher.noOrg')

  // Single org: show a static label, no dropdown.
  if (me.memberships.length <= 1) {
    return (
      <div className="hidden items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm sm:flex">
        <Building2 className="h-4 w-4 text-text-secondary" />
        <span className="text-text-primary">{activeName}</span>
      </div>
    )
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-elevated"
      >
        <Building2 className="h-4 w-4 text-text-secondary" />
        <span className="max-w-[140px] truncate">{activeName}</span>
        <ChevronDown className="h-3.5 w-3.5 text-text-secondary" />
      </button>
      {open && (
        // left-0 so the panel extends rightward into open space (the header)
        // rather than leftward under the sidebar. z-[60] sits above the
        // sidebar's z-50 for the mobile overlay case.
        <div className="absolute left-0 top-full z-[60] mt-2 w-64 rounded-lg border border-border bg-surface shadow-lg">
          <ul className="max-h-64 overflow-y-auto py-1">
            {me.memberships.map((m) => {
              const isActive = m.org_id === me.active_org_id
              return (
                <li key={m.org_id}>
                  <button
                    onClick={() => {
                      if (!isActive) switchOrg.mutate(m.org_id)
                      setOpen(false)
                    }}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-surface-elevated"
                  >
                    <span className="flex-1 truncate text-left">{m.org?.name ?? m.org_id}</span>
                    {isActive && <Check className="h-4 w-4 text-success" />}
                  </button>
                </li>
              )
            })}
          </ul>
          <div className="border-t border-border p-1">
            <Link
              to="/app/organizations"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded px-3 py-2 text-sm text-text-secondary hover:bg-surface-elevated hover:text-text-primary"
            >
              <Plus className="h-4 w-4" /> {t('orgSwitcher.manageOrgs')}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
